import React, { useEffect, useRef, useState } from "react";
import { Routes, Route, Navigate, useNavigate, useParams } from "react-router-dom";
import { Compass, Search, X, PanelLeftClose, PanelLeftOpen } from "lucide-react";
import ListView from "./ListView";
import FormView from "./FormView";
import TableFormView from "./TableFormView";
import PageView from "./PageView";
import NavigationView from "../components/NavigationView";
import TopBanner from "../components/common/TopBanner";
import CommandCenter from "./CommandCenter";
import AIArchitectApplyLab from "./AIArchitectApplyLab";
import AIUsageDashboard from "./AIUsageDashboard";
import FuelMap from "./FuelMap";
import PrivateRoute from "../components/PrivateRoute";

const RouteWrapper = ({ component: Component }) => {
  const { tableName, recordId } = useParams();
  const key = tableName + (recordId || "");
  return <Component key={key} />;
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [collapsed, setCollapsed] = useState(
    () => localStorage.getItem("nav_collapsed") === "1"
  );
  const searchRef = useRef(null);
  const isSuperAdmin = (() => {
    try {
      return JSON.parse(localStorage.getItem("roles") || "[]").includes("super_admin");
    } catch {
      return false;
    }
  })();

  useEffect(() => {
    localStorage.setItem("nav_collapsed", collapsed ? "1" : "0");
  }, [collapsed]);

  // ⌘/Ctrl+K to focus search
  useEffect(() => {
    const onKey = (e) => {
      const isK = e.key.toLowerCase() === "k";
      const meta = e.metaKey || e.ctrlKey;
      if (meta && isK) {
        e.preventDefault();
        if (searchRef.current) {
          searchRef.current.focus();
          searchRef.current.select();
        }
      }
      if (e.key === "Escape" && document.activeElement === searchRef.current) {
        searchRef.current.blur();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div style={styles.page}>
      <TopBanner />
      <div style={styles.frame}>
        {/* Sidebar */}
        <aside
          style={{
            ...styles.sidebar,
            width: collapsed ? 72 : 260,
          }}
        >
          <div style={styles.sidebarInner}>
            <div style={styles.sidebarHeader}>
              {!collapsed && (
                <h2 style={styles.sidebarTitle}>
                  <Compass size={15} color="#5eead4" />
                  Navigator
                </h2>
              )}
              <button
                onClick={() => setCollapsed((v) => !v)}
                title={collapsed ? "Expand menu" : "Collapse menu"}
                aria-label={collapsed ? "Expand menu" : "Collapse menu"}
                style={styles.collapseBtn}
                className="nav-item"
              >
                {collapsed ? <PanelLeftOpen size={15} /> : <PanelLeftClose size={15} />}
              </button>
            </div>

            {!collapsed && (
              <div style={styles.searchWrap}>
                <div style={styles.searchInner}>
                  <Search size={14} style={styles.searchIcon} />
                  <input
                    ref={searchRef}
                    type="text"
                    placeholder="Search modules… (⌘/Ctrl+K)"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={styles.searchInput}
                  />
                  {search && (
                    <button
                      type="button"
                      aria-label="Clear"
                      onClick={() => setSearch("")}
                      style={styles.clearBtn}
                    >
                      <X size={13} />
                    </button>
                  )}
                </div>
              </div>
            )}

            <NavigationView
              onTableClick={(t) => navigate(`/dashboard/${t}/list_view`)}
              searchTerm={search}
              collapsed={collapsed}
            />
          </div>
        </aside>

        {/* Content */}
        <main style={styles.content}>
          <div style={styles.contentInner}>
            <Routes>
              <Route
                path=":tableName/list_view"
                element={<RouteWrapper component={ListView} />}
              />
              <Route
                path=":tableName/form_view/:recordId"
                element={<RouteWrapper component={FormView} />}
              />
              <Route path="tables/form_view/:recordId" element={<TableFormView />} />
              <Route path="pages/:slug" element={<PageView />} />
              <Route
                path="ai-architect-lab/:sessionId?"
                element={
                  <PrivateRoute requireRoles={["super_admin"]}>
                    <AIArchitectApplyLab embedded />
                  </PrivateRoute>
                }
              />
              <Route
                path="ai-usage"
                element={
                  <PrivateRoute requireRoles={["super_admin"]}>
                    <AIUsageDashboard />
                  </PrivateRoute>
                }
              />
              <Route path="fuel-map" element={<FuelMap />} />
              {/* Dispatch Console moved to its own full-viewport /dispatch
                  route (no Navigator/TopBanner chrome) -- this just catches
                  old bookmarks/nav links to the previous nested path. */}
              <Route path="dispatch-board" element={<Navigate to="/dispatch" replace />} />
              <Route
                path=""
                element={
                  isSuperAdmin ? (
                    <CommandCenter embedded />
                  ) : (
                    <h2 style={styles.emptyHint}>Select a table to view records</h2>
                  )
                }
              />
            </Routes>
          </div>
        </main>
      </div>
    </div>
  );
};

/* ---------- styles ---------- */
const styles = {
  page: {
    display: "flex",
    flexDirection: "column",
    height: "100vh",
  },
  frame: {
    display: "flex",
    flexDirection: "row",
    alignItems: "stretch",
    flex: "1 1 auto",
    minHeight: 0,
    background: "#0b1020",
  },

  /* Sidebar */
  sidebar: {
    background: "linear-gradient(180deg, #111827 0%, #0f172a 100%)",
    color: "#e5e7eb",
    borderRight: "1px solid rgba(255,255,255,0.06)",
    overflow: "hidden",
    minHeight: 0,
    flexShrink: 0,
    boxShadow: "4px 0 16px rgba(2,6,23,0.25)",
    position: "relative",
    zIndex: 2,
    transition: "width .18s ease",
  },
  sidebarInner: {
    height: "100%",
    minHeight: 0,
    display: "flex",
    flexDirection: "column",
    padding: 12,
    gap: 8,
  },
  sidebarHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 8,
    background: "rgba(255,255,255,0.04)",
    border: "1px solid rgba(255,255,255,0.06)",
    borderRadius: 12,
    padding: "10px 12px",
    position: "sticky",
    top: 0,
    zIndex: 3,
    backdropFilter: "blur(6px)",
    flexShrink: 0,
  },
  sidebarTitle: {
    margin: 0,
    display: "flex",
    alignItems: "center",
    gap: 8,
    fontSize: 12.5,
    letterSpacing: ".08em",
    textTransform: "uppercase",
    color: "rgba(255,255,255,0.85)",
    fontWeight: 800,
  },
  collapseBtn: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    border: "1px solid rgba(255,255,255,0.14)",
    background: "rgba(255,255,255,0.06)",
    color: "#e5e7eb",
    borderRadius: 10,
    padding: "7px 8px",
    cursor: "pointer",
  },

  searchWrap: { paddingTop: 8, paddingBottom: 6, flexShrink: 0 },
  searchInner: {
    position: "relative",
    display: "flex",
    alignItems: "center",
  },
  searchIcon: {
    position: "absolute",
    left: 12,
    color: "rgba(229,231,235,.5)",
    pointerEvents: "none",
  },
  searchInput: {
    width: "100%",
    boxSizing: "border-box",
    padding: "10px 36px 10px 34px",
    borderRadius: 8,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.06)",
    color: "#e5e7eb",
    fontSize: 13.5,
    outline: "none",
  },
  clearBtn: {
    position: "absolute",
    right: 8,
    display: "inline-flex",
    border: "none",
    background: "transparent",
    color: "rgba(229,231,235,.8)",
    cursor: "pointer",
    padding: 2,
  },
  /* Content */
  content: {
    flex: "1 1 auto",
    minWidth: 0,
    minHeight: 0,
    overflow: "hidden",
    background: "linear-gradient(180deg, #0b1020 0%, #0f1426 100%)",
    position: "relative",
  },
  contentInner: {
    height: "100%",
    overflow: "auto",
    padding: 20,
    boxSizing: "border-box",
  },

  /* Empty text */
  emptyHint: {
    color: "#e5e7eb",
    fontWeight: 600,
    letterSpacing: ".2px",
  },
};

export default Dashboard;
